import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../index.css';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Save, UploadCloud, Copy, Pencil } from 'lucide-react';
import { useAuth } from '../context/authContext';
import { useToast } from '../components/ui/use-toast';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { WorkflowEditorSkeleton } from '../components/ui/card-skeletons';
import AsyncContent from '../components/ui/AsyncContent';
import WorkflowBuilder from '../components/workflows/WorkflowBuilder';
import {
    versionToLocalTiers,
    localTiersToDto,
    serializeDefinition,
    validateTiers,
} from '../components/workflows/workflowValidation';
import { getUsers } from '../services/user.service';
import { getGroups } from '../services/group.service';
import {
    getWorkflow,
    updateWorkflow,
    saveDraftVersion,
    publishVersion,
} from '../services/workflow.service';

const WorkflowEditorPage = () => {

    const { workflowId } = useParams();
    const { user } = useAuth();
    const { toast } = useToast();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [workflow, setWorkflow] = useState(null);
    const [users, setUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [name, setName] = useState('');
    const [isEditingName, setIsEditingName] = useState(false);
    const [tiers, setTiers] = useState([]);
    const [savedDefinition, setSavedDefinition] = useState(null);
    const nameInputRef = useRef(null);

    const isArchived = workflow?.status === 'ARCHIVED';

    const loadWorkflow = async () => {
        const response = await getWorkflow(user.token, user.organisationId, workflowId);
        if (response && response.status === 200) {
            const wf = response.data.data;
            setWorkflow(wf);
            setName(wf.name);
            const versions = wf.versions || [];
            const draft = versions.find((v) => v.status === 'DRAFT');
            const published = versions.find((v) => v.status === 'PUBLISHED');
            const loadedTiers = versionToLocalTiers(draft || published);
            setTiers(loadedTiers);
            setSavedDefinition(serializeDefinition(loadedTiers));
        } else {
            const message = response?.data?.error?.message || 'Could not load workflow';
            toast({ title: 'Error', description: message, variant: 'destructive' });
        }
    };

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            const [usersRes, groupsRes] = await Promise.all([
                getUsers(user.token, user.organisationId),
                getGroups(user.token, user.organisationId),
            ]);
            if (usersRes && usersRes.status === 200) setUsers(usersRes.data.data);
            if (groupsRes && groupsRes.status === 200) setGroups(groupsRes.data.data);
            await loadWorkflow();
            setLoading(false);
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workflowId, user.token, user.organisationId]);

    const { errors } = useMemo(
        () => validateTiers(tiers, users, groups),
        [tiers, users, groups]
    );

    const isDirty = useMemo(() => {
        if (savedDefinition === null) return false;
        return serializeDefinition(tiers) !== savedDefinition;
    }, [tiers, savedDefinition]);

    const publishedVersion = workflow?.versions?.find((v) => v.status === 'PUBLISHED');
    const draftVersion = workflow?.versions?.find((v) => v.status === 'DRAFT');

    const handleSaveName = async () => {
        if (!name.trim()) return;
        if (name.trim() === workflow.name) {
            setIsEditingName(false);
            return;
        }
        const response = await updateWorkflow(user.token, user.organisationId, workflowId, { name: name.trim() });
        if (response && response.status === 200) {
            toast({ title: 'Success', description: 'Workflow renamed', variant: 'success' });
            setIsEditingName(false);
            await loadWorkflow();
        } else {
            const message = response?.data?.error?.message || 'Could not rename workflow';
            toast({ title: 'Error', description: message, variant: 'destructive' });
        }
    };

    useEffect(() => {
        if (isEditingName) {
            nameInputRef.current?.focus();
            nameInputRef.current?.select();
        }
    }, [isEditingName]);

    // Saves the current definition into the editable DRAFT version and returns the
    // updated WorkflowView (or null on failure).
    const persistDraft = async () => {
        const dto = localTiersToDto(tiers);
        const response = await saveDraftVersion(user.token, user.organisationId, workflowId, dto);
        if (response && (response.status === 200 || response.status === 201)) {
            return response.data.data;
        }
        const message = response?.data?.error?.message || 'Could not save draft';
        toast({ title: 'Error', description: message, variant: 'destructive' });
        return null;
    };

    const handleSaveDraft = async () => {
        if (!isDirty) return;
        if (errors.length > 0) {
            toast({ title: 'Cannot save', description: 'Please resolve the highlighted issues first.', variant: 'destructive' });
            return;
        }
        setSaving(true);
        const updated = await persistDraft();
        setSaving(false);
        if (updated) {
            toast({ title: 'Saved', description: 'Draft version saved', variant: 'success' });
            await loadWorkflow();
        }
    };

    const handlePublish = async () => {
        if (!isDirty) return;
        if (errors.length > 0) {
            toast({ title: 'Cannot publish', description: 'Please resolve the highlighted issues first.', variant: 'destructive' });
            return;
        }
        setSaving(true);
        // Persist the latest definition first so we publish exactly what's on screen.
        const updated = await persistDraft();
        if (!updated) {
            setSaving(false);
            return;
        }
        const draft = (updated.versions || []).find((v) => v.status === 'DRAFT');
        if (!draft) {
            setSaving(false);
            toast({ title: 'Error', description: 'No draft version to publish.', variant: 'destructive' });
            return;
        }
        const response = await publishVersion(user.token, user.organisationId, workflowId, draft.id);
        setSaving(false);
        if (response && (response.status === 200 || response.status === 201)) {
            toast({ title: 'Published', description: 'Workflow version published and activated', variant: 'success' });
            await loadWorkflow();
        } else {
            const message = response?.data?.error?.message || 'Could not publish version. Check approver eligibility.';
            toast({ title: 'Publish failed', description: message, variant: 'destructive' });
        }
    };

    const copyId = () => {
        navigator.clipboard?.writeText(workflowId);
        toast({ title: 'Copied', description: 'Workflow ID copied to clipboard', variant: 'success' });
    };

    return (
        <div className="App">
            <AsyncContent loading={loading} skeleton={<WorkflowEditorSkeleton />}>
                {!workflow ? (
                    <>
                        <Link className="underline flex flex-row items-center" to="/workflows">
                            <ArrowLeft className="mr-1 w-5" /> Back to workflows
                        </Link>
                        <p className="mt-6">Workflow not found.</p>
                    </>
                ) : (
            <div className="max-w-5xl mx-auto flex flex-col gap-6">
                <Link className="underline flex flex-row items-center w-fit" to="/workflows">
                    <ArrowLeft className="mr-1 w-5" /> Back to workflows
                </Link>

                <Card>
                    <CardHeader>
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <h3 className="my-0">Edit Workflow</h3>
                                <Badge variant={workflow.status === 'ACTIVE' ? 'success' : workflow.status === 'DRAFT' ? 'warning' : 'muted'}>
                                    {workflow.status}
                                </Badge>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <span>Workflow ID:</span>
                                <code className="text-xs bg-slate-100 rounded px-2 py-1">{workflow.workflowId}</code>
                                <button type="button" onClick={copyId} className="text-gray-400 hover:text-gray-600" title="Copy">
                                    <Copy className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-4">
                        <div className="flex flex-wrap items-end gap-3">
                            <div className="flex flex-col gap-1 grow max-w-md">
                                <label className="text-xs text-gray-500">Name</label>
                                <div className="flex items-center gap-1">
                                    <Input
                                        ref={nameInputRef}
                                        value={name}
                                        disabled={isArchived || !isEditingName}
                                        onChange={(e) => setName(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && isEditingName) {
                                                e.preventDefault();
                                                handleSaveName();
                                            }
                                            if (e.key === 'Escape' && isEditingName) {
                                                setName(workflow.name);
                                                setIsEditingName(false);
                                            }
                                        }}
                                        className="flex-1"
                                    />
                                    {!isArchived && (
                                        isEditingName ? (
                                            <Button
                                                variant="ghost"
                                                className="p-2 text-yavrio-green shrink-0"
                                                disabled={!name.trim()}
                                                onClick={handleSaveName}
                                                title="Save name"
                                            >
                                                <Save className="h-4 w-4" />
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="ghost"
                                                className="p-2 text-yavrio-blue shrink-0"
                                                onClick={() => setIsEditingName(true)}
                                                title="Edit name"
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                        {(draftVersion || publishedVersion) && (
                            <p className="text-xs text-slate-400 mb-0 text-right">
                                v{(draftVersion || publishedVersion).version}
                            </p>
                        )}
                    </CardContent>
                </Card>

                {isArchived && (
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                        <p className="text-sm text-gray-600 mb-0">This workflow is archived and read-only.</p>
                    </div>
                )}

                <Card>
                    <CardHeader>
                        <h3 className="my-0">Value Tiers &amp; Approval Steps</h3>
                        <p className="text-sm text-gray-500 mb-0">
                            These rules determine who can approve each payment. Individual user limits are not
                            checked. Each approval covers the full payment total; requiring two approvals does
                            not divide the amount between approvers.
                        </p>
                    </CardHeader>
                    <CardContent>
                        <WorkflowBuilder
                            tiers={tiers}
                            setTiers={setTiers}
                            users={users}
                            groups={groups}
                            disabled={isArchived}
                        />
                    </CardContent>
                </Card>

                {!isArchived && (
                    <div className="flex flex-wrap items-center justify-between gap-4 sticky bottom-0 bg-white/80 backdrop-blur px-6 py-4 border-t border-slate-200">
                        <p className="text-xs text-gray-400 mb-0 max-w-xl">
                            Updated workflows will apply to new payments registered in the authorisation portal. Existing payments will follow the workflow version at the time of their creation.
                        </p>
                        <div className="flex items-center gap-3 shrink-0">
                            {!publishedVersion && (
                                <Button variant="outline" className="gap-2" disabled={saving || errors.length > 0 || !isDirty} onClick={handleSaveDraft}>
                                    <Save className="w-4 h-4" /> Save Draft
                                </Button>
                            )}
                            <Button className="yvr-button gap-2" disabled={saving || errors.length > 0 || !isDirty} onClick={handlePublish}>
                                <UploadCloud className="w-4 h-4" /> Save &amp; Publish
                            </Button>
                        </div>
                    </div>
                )}
            </div>
                )}
            </AsyncContent>
        </div>
    );
};

export default WorkflowEditorPage;
